import React, { useState, useMemo } from 'react';
import { Diamond } from 'lucide-react';
import { diffDays, xDias } from './constants';
import { hitoEstado, HITO_COLOR_DEFAULT } from '../utils/displays';

// ---------- Hitos en el eje temporal del Gantt/Roadmap ----------
//
// Un hito es un punto en el tiempo (su 'fecha'). Se dibuja como un ROMBO
// (el icono Diamond de lucide) en la x de esa fecha. El relleno codifica el
// estado, igual que el HitoChip y el ProjectBar del modulo:
//   - completado -> relleno solido con el color del hito
//   - pendiente  -> contorno (outline), interior blanco
//   - vencido +  -> rose (#e11d48), pendiente
//
// Reutilizable en tres contextos:
//   - HitosLane    : fila/lane dedicada arriba del timeline (Gantt de proyecto).
//   - HitosOverlay : rombos pequenos superpuestos sobre la barra de un proyecto
//                    (Gantt global y Roadmap), donde no hay lane propia.

const COLOR_VENCIDO = '#e11d48';

const ESTADO_LABEL = {
  completado: 'Completado',
  vencido: 'Vencido',
  pendiente: 'Pendiente',
};

// Color efectivo de un hito segun su estado.
const colorDeHito = (hito) => {
  const { vencido } = hitoEstado(hito);
  if (vencido) return COLOR_VENCIDO;
  return hito.color || HITO_COLOR_DEFAULT;
};

// x (centro) de un hito dentro del canvas, alineada al centro del dia — mismo
// criterio que la linea de hoy / preview (offset*dayWidth + dayWidth/2). En el
// lienzo del Gantt se expresa con --dw (sigue a la camara sin renders); en el
// Roadmap, en px.
const hitoCenterX = (hito, firstDay, dayWidth, enLienzo = false) => {
  if (!hito?.fecha) return null;
  const f = new Date(hito.fecha + 'T00:00:00');
  const offset = diffDays(f, firstDay);
  return enLienzo ? xDias(offset + 0.5) : offset * dayWidth + dayWidth / 2;
};

// El glifo: rombo relleno si completado, contorno si no. lucide define `fill`
// como stroke-only por default, por eso lo forzamos por estilo inline (mismo
// truco que displays.js con el Flag).
const Rombo = ({ size, color, completado }) => (
  <Diamond
    size={size}
    color={color}
    strokeWidth={2}
    style={{ fill: completado ? color : '#ffffff' }}
  />
);

// Rombo individual con popover al hover. `cx` es el centro horizontal en px,
// `cy` el centro vertical respecto al contenedor. `size` = lado del rombo.
const HitoDiamond = ({ hito, cx, cy, size = 12 }) => {
  const [hover, setHover] = useState(false);
  const { key } = hitoEstado(hito);
  const color = colorDeHito(hito);
  const completado = key === 'completado';

  const fechaTexto = hito.fecha
    ? new Date(hito.fecha + 'T00:00:00').toLocaleDateString('es-VE', {
        day: 'numeric', month: 'short', year: 'numeric',
      })
    : null;

  return (
    <div
      className="absolute -translate-x-1/2 -translate-y-1/2 pointer-events-auto"
      style={{ left: cx, top: cy, zIndex: hover ? 30 : 12 }}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
    >
      <Rombo size={size} color={color} completado={completado} />

      {hover && (
        <div
          className="absolute left-1/2 -translate-x-1/2 bottom-full mb-1.5 w-max max-w-[220px] bg-white rounded-lg shadow-xl ring-1 ring-slate-200 px-3 py-2 z-40"
          role="tooltip"
        >
          <div className="flex items-start gap-1.5">
            <span className="mt-0.5 flex-shrink-0">
              <Rombo size={9} color={color} completado={completado} />
            </span>
            <span className="text-xs font-semibold text-slate-800 leading-snug break-words">
              {hito.nombre}
            </span>
          </div>
          <div className="mt-1.5 flex items-center gap-2 text-[10px] tabular-nums">
            <span className="font-semibold" style={{ color }}>
              {ESTADO_LABEL[key]}
            </span>
            {typeof hito.tareas_total === 'number' && (
              <span className="text-slate-500">
                {hito.tareas_hechas ?? 0}/{hito.tareas_total} tareas
              </span>
            )}
          </div>
          {fechaTexto && (
            <div className="mt-0.5 text-[10px] text-slate-400 tabular-nums">{fechaTexto}</div>
          )}
        </div>
      )}
    </div>
  );
};

// ---------- Lane dedicada (Gantt de proyecto) ----------
//
// Una fila propia para los hitos del proyecto, arriba de las categorias. Lleva
// su contraparte en el sidebar (HitosLaneSidebar) para que la altura cuadre.

const HitosLane = ({ hitos, firstDay, height }) => {
  const items = useMemo(() => {
    return (hitos || [])
      .filter((h) => h && h.fecha)
      .map((h) => ({ hito: h, cx: hitoCenterX(h, firstDay, null, true) }))
      .filter((x) => x.cx != null);
  }, [hitos, firstDay]);

  return (
    <div className="relative border-b border-slate-100 bg-violet-50/20" style={{ height }}>
      {items.map(({ hito, cx }) => (
        <HitoDiamond key={hito.id} hito={hito} cx={cx} cy={height / 2} size={13} />
      ))}
    </div>
  );
};

// Etiqueta de la lane de hitos en el sidebar (espejo de la altura de la lane).
export const HitosLaneSidebar = ({ count, height }) => (
  <div
    className="w-full flex items-center gap-2 px-4 bg-violet-50/30 border-b border-slate-100"
    style={{ height }}
  >
    <span className="flex items-center justify-center w-3 flex-shrink-0">
      <Diamond size={11} color={HITO_COLOR_DEFAULT} strokeWidth={2.2} />
    </span>
    <span className="text-[11px] font-bold tracking-[0.1em] uppercase text-violet-600/80">Hitos</span>
    <span className="text-[10px] font-medium text-slate-400 tabular-nums ml-auto">{count}</span>
  </div>
);

// ---------- Overlay sobre una barra (Gantt global / Roadmap) ----------
//
// No hay lane propia: los rombos van encima de la barra del proyecto, anclados
// a su fecha. `cy` se centra en la altura de la fila/barra. `size` chico para
// no tapar la barra.

export const HitosOverlay = ({ hitos, firstDay, dayWidth, cy, size = 10, enLienzo = false }) => {
  const items = useMemo(() => {
    return (hitos || [])
      .filter((h) => h && h.fecha)
      .map((h) => ({ hito: h, cx: hitoCenterX(h, firstDay, dayWidth, enLienzo) }))
      .filter((x) => x.cx != null);
  }, [hitos, firstDay, dayWidth, enLienzo]);

  if (items.length === 0) return null;

  return (
    <>
      {items.map(({ hito, cx }) => (
        <HitoDiamond key={hito.id} hito={hito} cx={cx} cy={cy} size={size} />
      ))}
    </>
  );
};

export default HitosLane;
